import { FC, useEffect } from "react";
import { FormInsuredOfficerProps } from "./TypesInsuredOfficers";
import {
  Col,
  Font,
  Input,
  InputDate,
  Row,
  Select,
  Switch,
} from "../../../TrueUI";
import { rowWithNoMarginNorGutter } from "../../../TrueUI/Grids/Row";
import { selectOptionsByEnumValues } from "../../../../utilities/enumFunctions";
import { StatusEnums } from "../../../../dtos/status-enums";
import {
  getOfficerIclExclSelectOptions,
  OFFICER_EXCLUDE,
} from "../../../../utilities/staticDataFunctions";

const FormInsuredOfficer: FC<FormInsuredOfficerProps> = ({
  officer,
  setOfficer,
  errors,
  officerTypes,
}) => {
  const isDisabledByOfficerType =
    (officer?.officerInclExcl ?? "") === OFFICER_EXCLUDE;

  useEffect(() => {
    if (officer?.officerInclExcl === OFFICER_EXCLUDE) {
      setOfficer({
        ...officer,
        payrollBilling: false,
        annualBilledPayroll: 0,
      });
    }
  }, [officer?.officerInclExcl]);
  return (
    <>
      <Row {...rowWithNoMarginNorGutter}>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 6, lg: 5, xl: 5 }}
        >
          <Select
            id="insured-officer-record-type"
            name="insured-officer-record-type"
            label="Record Type"
            options={officerTypes}
            value={officer?.officerType}
            errorMessage={errors?.officerType}
            onChange={(value) => {
              setOfficer({ ...officer, officerType: value });
            }}
            firstOptionAsDefault={false}
            labelFontType={"BOLD_CAPTION"}
          />
        </Col>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 12, lg: 2, xl: 2 }}
        ></Col>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 6, lg: 5, xl: 5 }}
        >
          <Select
            id="insured-officer-include-exlude"
            name="insured-officer-include-exlude"
            label="Include or Exclude"
            options={getOfficerIclExclSelectOptions()}
            value={officer?.officerInclExcl}
            errorMessage={errors?.officerInclExcl}
            onChange={(value) => {
              setOfficer({ ...officer, officerInclExcl: value });
            }}
            firstOptionAsDefault={false}
            labelFontType={"BOLD_CAPTION"}
          />
        </Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col>
          <Input
            id="insured-officer-name"
            name="insured-officer-name"
            label="Name"
            value={officer?.officerName}
            errorMessage={errors?.officerName}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, officerName: value });
            }}
            maxLength={100}
          />
        </Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col>
          <Input
            id="insured-officer-title"
            name="insured-officer-title"
            label="Title"
            value={officer?.officerTitle}
            errorMessage={errors?.officerTitle}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, officerTitle: value });
            }}
            maxLength={50}
          />
        </Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 6, lg: 3, xl: 3 }}>
          <Input
            id="insured-officer-owner-pct"
            name="insured-officer-owner-pct"
            label="Ownership Percentage"
            value={officer?.ownershipPct}
            errorMessage={errors?.ownershipPct}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, ownershipPct: value });
            }}
            stringNumberMask="000000000000000000"
            decimalScale={1}
            allowNegatives={false}
            type={"fixedCurrency"}
            prefix=""
          />
        </Col>
        <Col breakpoints={{ md: 12, lg: 4, xl: 4 }}>
          <Select
            id="insured-officer-status"
            name="insured-officer-status"
            label="Status"
            options={selectOptionsByEnumValues(StatusEnums).filter(
              (o) => o.intValue !== StatusEnums.DELETED
            )}
            value={officer?.officerStatus}
            errorMessage={errors?.officerStatus}
            onChange={(value) => {
              setOfficer({ ...officer, officerStatus: value });
            }}
            firstOptionAsDefault={false}
            labelFontType={"BOLD_CAPTION"}
          />
        </Col>
        <Col breakpoints={{ md: 12, lg: 3, xl: 3 }}>
          <Input
            id="insured-officer-class-code"
            name="insured-officer-class-code"
            label="Class Code"
            value={officer?.classCode}
            errorMessage={errors?.classCode}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, classCode: value });
            }}
            maxLength={20}
          />
        </Col>
        <Col breakpoints={{ md: 6, lg: 2, xl: 2 }}></Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 6, lg: 5, xl: 5 }}
        >
          <InputDate
            id="insured-officer-effective-date"
            name="insured-officer-effective-date"
            label="Effective Date"
            value={officer?.effectiveDate}
            errorMessage={errors?.effectiveDate}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, effectiveDate: value });
            }}
          />
        </Col>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 12, lg: 2, xl: 2 }}
        ></Col>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 6, lg: 5, xl: 5 }}
        >
          <InputDate
            id="insured-officer-expiration-date"
            name="insured-officer-expiration-date"
            label="Expiration Date"
            value={officer?.expirationDate}
            errorMessage={errors?.expirationDate}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, expirationDate: value });
            }}
          />
        </Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col horizontalAlign={"flex-start"}>
          <Font fontType={"BOLD_TITLE"}>
            This information used only if the officer is included and reporting
            payroll
          </Font>
        </Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 6, lg: 5, xl: 5 }}
        >
          <Switch
            id="insured-officer-payroll-report-billing"
            name="insured-officer-payroll-report-billing"
            label="Payroll Report Billing?"
            isChecked={officer?.payrollBilling}
            onChangeIsChecked={(value) => {
              setOfficer({ ...officer, payrollBilling: value });
            }}
            control="checkbox"
            readOnly={isDisabledByOfficerType}
            labelFontType={"BOLD_CAPTION"}
          />
        </Col>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 12, lg: 2, xl: 2 }}
        ></Col>
        <Col
          horizontalAlign={"flex-start"}
          breakpoints={{ md: 6, lg: 5, xl: 5 }}
        >
          <Input
            id="insured-officer-anual-billed-payroll"
            name="insured-officer-anual-billed-payroll"
            label="Annual Billed Payroll"
            value={officer?.annualBilledPayroll}
            errorMessage={errors?.annualBilledPayroll}
            onChangeRawValue={(value) => {
              setOfficer({ ...officer, annualBilledPayroll: value });
            }}
            maxNumericValue={1000000}
            decimalScale={2}
            allowNegatives={false}
            type={"fixedCurrency"}
            prefix=""
            readOnly={isDisabledByOfficerType}
          />
        </Col>
      </Row>
    </>
  );
};

export default FormInsuredOfficer;
